import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { STATIC } from '../../lib/queryPresets';
import * as svc from '../../services/cabinet/cabinetService';

export const cabinetKeys = {
  folders: ['cabinet-folders'] as const,
  files: (folderId: string) => ['cabinet-files', folderId] as const,
  tags: ['cabinet-tags'] as const,
  allFileTags: ['cabinet-file-tags'] as const,
  members: (folderId: string) => ['cabinet-members', folderId] as const,
};

// ─── Folders ────────────────────────────────────────────────────────────

export function useCabinetFolders() {
  return useQuery({
    ...STATIC,
    queryKey: cabinetKeys.folders,
    queryFn: svc.listFolders,
  });
}

export function useCreateCabinetFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: svc.createFolder,
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinetKeys.folders }),
  });
}

export function useRenameCabinetFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ folderId, name }: { folderId: string; name: string }) =>
      svc.renameFolder(folderId, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinetKeys.folders }),
  });
}

export function useUpdateCabinetFolderEmoji() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ folderId, emoji }: { folderId: string; emoji: string }) =>
      svc.updateFolderEmoji(folderId, emoji),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinetKeys.folders }),
  });
}

export function useDeleteCabinetFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: svc.deleteFolder,
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinetKeys.folders }),
  });
}

// ─── Files ──────────────────────────────────────────────────────────────

export function useCabinetFiles(folderId: string | null) {
  return useQuery({
    ...STATIC,
    queryKey: cabinetKeys.files(folderId ?? ''),
    queryFn: () => svc.listFiles(folderId!),
    enabled: !!folderId,
  });
}

export function useUploadCabinetFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: svc.uploadFile,
    onSuccess: (file) => qc.invalidateQueries({ queryKey: cabinetKeys.files(file.folder_id) }),
  });
}

export function useDeleteCabinetFile(folderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: svc.deleteFile,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cabinetKeys.files(folderId) });
      qc.invalidateQueries({ queryKey: cabinetKeys.allFileTags });
    },
  });
}

export function useRenameCabinetFile(folderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId, filename }: { fileId: string; filename: string }) =>
      svc.renameFile(fileId, filename),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinetKeys.files(folderId) }),
  });
}

// ─── Tags ────────────────────────────────────────────────────────────────

export function useCabinetTags() {
  return useQuery({ ...STATIC, queryKey: cabinetKeys.tags, queryFn: svc.listTags });
}

export function useAllCabinetFileTags() {
  return useQuery({
    ...STATIC,
    queryKey: cabinetKeys.allFileTags,
    queryFn: svc.listAllFileTags,
  });
}

export function useCreateCabinetTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, color, createdBy }: { name: string; color: string; createdBy: string | null }) =>
      svc.createTag(name, color, createdBy),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinetKeys.tags }),
  });
}

export function useDeleteCabinetTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: svc.deleteTag,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cabinetKeys.tags });
      qc.invalidateQueries({ queryKey: cabinetKeys.allFileTags });
    },
  });
}

export function useSetCabinetFileTags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId, tagIds }: { fileId: string; tagIds: string[] }) =>
      svc.setFileTags(fileId, tagIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinetKeys.allFileTags }),
  });
}

// ─── Members ────────────────────────────────────────────────────────────

export function useCabinetRootMembers(rootFolderId: string | null) {
  return useQuery({
    ...STATIC,
    queryKey: cabinetKeys.members(rootFolderId ?? ''),
    queryFn: () => svc.listRootMembers(rootFolderId!),
    enabled: !!rootFolderId,
  });
}

export function useUpsertCabinetMember(rootFolderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: svc.upsertMember,
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinetKeys.members(rootFolderId) }),
  });
}

export function useRemoveCabinetMember(rootFolderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ collaboratorId }: { collaboratorId: string }) =>
      svc.removeMember(rootFolderId, collaboratorId),
    onSuccess: () => qc.invalidateQueries({ queryKey: cabinetKeys.members(rootFolderId) }),
  });
}
